import React, { useState } from 'react';
import { Modal, Header, Button, Icon, Segment, Grid, Label, Accordion, Message, Divider } from 'semantic-ui-react';

/**
 * EnhancedCharactersDiagnostic - Diagnostic modal for enhanced characters API response
 * @param {Object} props - Component props
 * @param {boolean} props.isOpen - Whether the modal is open
 * @param {Function} props.onClose - Callback to close the modal
 * @param {Object} props.apiResponse - The API response data to display
 * @param {Array} props.characters - The processed characters array
 * @param {string} props.title - Modal title
 * @returns {JSX.Element} Diagnostic modal component
 */
const EnhancedCharactersDiagnostic = ({
  isOpen,
  onClose,
  apiResponse,
  characters = [],
  title = "Enhanced Characters API Diagnostic"
}) => {
  const [activeIndex, setActiveIndex] = useState(-1);
  const [showRawData, setShowRawData] = useState(false);

  const handleAccordionClick = (e, titleProps) => {
    const { index } = titleProps;
    const newIndex = activeIndex === index ? -1 : index;
    setActiveIndex(newIndex);
  };

  // Calculate summary statistics
  const summary = {
    totalCharacters: characters.length,
    charactersWithVariations: characters.filter(c => c.variationCount > 0).length,
    charactersWithAssignments: characters.filter(c => c.assignmentStatus === 'assigned').length,
    charactersWithInterest: characters.filter(c => c.interestCount > 0).length,
    totalOpportunities: characters.reduce((sum, c) => sum + (c.opportunities?.length || 0), 0),
    totalInterest: characters.reduce((sum, c) => sum + (c.interestCount || 0), 0),
    totalVariations: characters.reduce((sum, c) => sum + (c.variationCount || 0), 0)
  };

  // Validation function
  const validateCharacter = (character) => {
    const validations = {
      hasBasicInfo: !!(character.name && character.uuid),
      hasVariationCount: character.variationCount !== undefined,
      hasAssignmentStatus: !!(character.assignmentStatus),
      hasOpportunities: Array.isArray(character.opportunities),
      hasInterestCount: character.interestCount !== undefined
    };

    const validCount = Object.values(validations).filter(Boolean).length;
    const totalChecks = Object.keys(validations).length;

    return {
      ...validations,
      score: validCount,
      total: totalChecks,
      percentage: Math.round((validCount / totalChecks) * 100)
    };
  };

  // Copy to clipboard function
  const copyToClipboard = (data) => {
    navigator.clipboard.writeText(JSON.stringify(data, null, 2));
  };

  return (
    <Modal
      open={isOpen}
      onClose={onClose}
      size="large"
      closeIcon
    >
      <Header icon="bug" content={title} />

      <Modal.Content scrolling>
        {/* Summary Section */}
        <div>
          <Header as="h3">
            <Icon name="chart bar" />
            API Response Summary
          </Header>
          <Grid style={{ paddingBottom: '0px' }}>
            <Grid.Row>
              <Grid.Column style={{ width: "auto" }}>
                <Label color="blue" size="large">
                  <Icon name="users" />
                  {summary.totalCharacters}
                  <Label.Detail>Characters</Label.Detail>
                </Label>
              </Grid.Column>
              <Grid.Column style={{ width: "auto" }}>
                <Label color={summary.charactersWithVariations > 0 ? "green" : "grey"} size="large" >
                  <Icon name="check circle" />
                  {summary.charactersWithVariations} / {summary.totalCharacters}
                  <Label.Detail>Have Variations</Label.Detail>
                </Label>
              </Grid.Column>
              <Grid.Column style={{ width: "auto" }}>
                <Label color="green" size="large">
                  <Icon name="sound" />
                  {summary.totalVariations}
                  <Label.Detail>Variations</Label.Detail>
                </Label>
              </Grid.Column>
              <Grid.Column style={{ width: "auto" }}>
                <Label color="orange" size="large">
                  <Icon name="briefcase" />
                  {summary.totalOpportunities}
                  <Label.Detail>Opportunities</Label.Detail>
                </Label>
              </Grid.Column>
              <Grid.Column style={{ width: "auto" }}>
                <Label color="purple" size="large">
                  <Icon name="heart" />
                  {summary.totalInterest}
                  <Label.Detail>Total Interest</Label.Detail>
                </Label>
              </Grid.Column>
            </Grid.Row>
            <Grid.Row style={{ paddingTop: '0px' }}>

              <Grid.Column style={{ width: "auto" }}>
                <Label color={summary.charactersWithAssignments > 0 ? "green" : "grey"}>
                  <Icon name="user" />
                  {summary.charactersWithAssignments} / {summary.totalCharacters}
                  <Label.Detail>Assigned</Label.Detail>
                </Label>
              </Grid.Column>
              <Grid.Column style={{ width: "auto" }}>
                <Label color={summary.charactersWithInterest > 0 ? "green" : "grey"}>
                  <Icon name="heart" />
                  {summary.charactersWithInterest} / {summary.totalCharacters}
                  <Label.Detail>Have Interest</Label.Detail>
                </Label>
              </Grid.Column>
            </Grid.Row>
          </Grid>
        </div>
        <Divider />
        {/* Character Details Accordion */}
        <div style={{ marginTop: '20px', maxWidth: '900px' }} >
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: '1em'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5em' }}>
              <Icon name="list" size="large" />
              <span style={{ fontSize: '1.28571429em', fontWeight: 'bold' }}>Character Details</span>
            </div>
            <Button
              size="mini"
              onClick={() => setShowRawData(!showRawData)}
              color={showRawData ? "red" : "green"}
            >
              <Icon name={showRawData ? "eye slash" : "eye"} />
              {showRawData ? "Hide Raw Data" : "Show Raw Data"}
            </Button>
          </div>


          <Accordion styled style={{ width: '900px' }}>
            {characters.map((character, index) => {
              const validation = validateCharacter(character);

              return (
                <div key={character.uuid || index}>
                  <Accordion.Title
                    active={activeIndex === index}
                    index={index}
                    onClick={handleAccordionClick}
                  >
                    <Icon name="dropdown" />
                    <strong>{character.name || 'Unnamed Character'}</strong>

                    {/* Validation Score */}
                    <Label
                      color={validation.percentage >= 80 ? "green" : validation.percentage >= 60 ? "yellow" : "red"}
                      size="mini"
                      style={{ marginLeft: '1em' }}
                    >
                      {validation.score}/{validation.total} checks
                    </Label>

                    {/* Quick Stats */}
                    <div style={{ float: 'right', marginRight: '2em' }}>
                      {character.variationCount > 0 && (
                        <Label size="tiny" color="blue">
                          <Icon name="sound" />
                          {character.variationCount}
                        </Label>
                      )}
                      {character.assignmentStatus === 'assigned' && (
                        <Label size="tiny" color="green">
                          <Icon name="user" />
                          Assigned
                        </Label>
                      )}
                      {character.interestCount > 0 && (
                        <Label size="tiny" color="purple">
                          <Icon name="heart" />
                          {character.interestCount}
                        </Label>
                      )}
                    </div>
                  </Accordion.Title>

                  <Accordion.Content active={activeIndex === index}>
                    <Grid columns={3} divided>
                      <Grid.Column width={5}>
                        <Header as="h4">Basic Information</Header>
                        <div style={{ marginBottom: '1em' }}>
                          <strong>UUID:</strong> {character.uuid || 'Missing'}
                          {validation.hasBasicInfo ?
                            <Icon name="check circle" color="green" style={{ marginLeft: '0.5em' }} /> :
                            <Icon name="times circle" color="red" style={{ marginLeft: '0.5em' }} />
                          }
                        </div>
                        <div style={{ marginBottom: '1em' }}>
                          <strong>Name:</strong> {character.name || 'Missing'}
                        </div>
                        <div style={{ marginBottom: '1em' }}>
                          <strong>Status:</strong> {character.status || 'Not set'}
                        </div>
                        <div style={{ marginBottom: '1em' }}>
                          <strong>Priority:</strong> {character.priority || 'Not set'}
                        </div>

                        <Header as="h4">Enhancement Data</Header>
                        <div style={{ marginBottom: '1em' }}>
                          <strong>Variation Count:</strong> {character.variationCount ?? 'Missing'}
                          {validation.hasVariationCount ?
                            <Icon name="check circle" color="green" style={{ marginLeft: '0.5em' }} /> :
                            <Icon name="times circle" color="red" style={{ marginLeft: '0.5em' }} />
                          }
                        </div>
                        <div style={{ marginBottom: '1em' }}>
                          <strong>Assignment Status:</strong> {character.assignmentStatus || 'Missing'}
                          {validation.hasAssignmentStatus ?
                            <Icon name="check circle" color="green" style={{ marginLeft: '0.5em' }} /> :
                            <Icon name="times circle" color="red" style={{ marginLeft: '0.5em' }} />
                          }
                        </div>
                        <div style={{ marginBottom: '1em' }}>
                          <strong>Interest Count:</strong> {character.interestCount ?? 'Missing'}
                          {validation.hasInterestCount ?
                            <Icon name="check circle" color="green" style={{ marginLeft: '0.5em' }} /> :
                            <Icon name="times circle" color="red" style={{ marginLeft: '0.5em' }} />
                          }
                        </div>
                      </Grid.Column>

                      <Grid.Column width={5}>
                        <Header as="h4">Variations ({character.variationCount || 0})</Header>
                        {character.variations && character.variations.length > 0 ? (
                          character.variations.map((variation, varIndex) => (
                            <div key={variation.uuid || varIndex} style={{ marginBottom: '0.5em', padding: '0.5em', backgroundColor: '#f0f8ff', borderRadius: '4px' }}>
                              <strong>{variation.name || 'Unnamed Variation'}</strong><br />
                              <small>
                                Status: {variation.status || 'Unknown'} |
                                Priority: {variation.priority || 'Medium'}
                                {variation.activeSampleUuid && <span> | Active Sample</span>}
                              </small>
                            </div>
                          ))
                        ) : (
                          <Message size="small" info>
                            {character.variationCount > 0
                              ? `${character.variationCount} variations (names not loaded)`
                              : 'No variations found'
                            }
                          </Message>
                        )}
                      </Grid.Column>

                      <Grid.Column width={6}>
                        <Header as="h4">Opportunities ({character.opportunities?.length || 0})</Header>
                        {validation.hasOpportunities ?
                          <Icon name="check circle" color="green" style={{ marginBottom: '0.5em' }} /> :
                          <Icon name="times circle" color="red" style={{ marginBottom: '0.5em' }} />
                        }
                        {character.opportunities?.length > 0 ? (
                          character.opportunities.map((opp, oppIndex) => (
                            <div key={opp.uuid || oppIndex} style={{ marginBottom: '0.5em', padding: '0.5em', backgroundColor: '#f8f9fa', borderRadius: '4px' }}>
                              <strong>{opp.title || 'Untitled'}</strong><br />
                              <small>Status: {opp.status || 'Unknown'} | Applications: {opp.applications?.length || 0}</small>
                            </div>
                          ))
                        ) : (
                          <Message size="small" info>No opportunities found</Message>
                        )}

                        {character.assignedActor && (
                          <>
                            <Header as="h4">Assigned Actor</Header>
                            <div style={{ padding: '0.5em', backgroundColor: '#e8f5e8', borderRadius: '4px' }}>
                              <strong>{character.assignedActor.firstName} {character.assignedActor.lastName}</strong><br />
                              <small>UUID: {character.assignedActor.uuid}</small>
                            </div>
                          </>
                        )}
                      </Grid.Column>
                    </Grid>

                    {showRawData && (
                      <>
                        <Divider />
                        <Header as="h4">
                          Raw Character Data
                          <Button
                            size="mini"
                            floated="right"
                            onClick={() => copyToClipboard(character)}
                          >
                            <Icon name="copy" />
                            Copy
                          </Button>
                        </Header>
                        <pre style={{
                          backgroundColor: '#f8f9fa',
                          padding: '1em',
                          borderRadius: '4px',
                          overflow: 'auto',
                          maxHeight: '300px',
                          fontSize: '12px'
                        }}>
                          {JSON.stringify(character, null, 2)}
                        </pre>
                      </>
                    )}
                  </Accordion.Content>
                </div>
              );
            })}
          </Accordion>
        </div>

        {/* Raw API Response Section */}
        {showRawData && apiResponse && (
          <Segment>
            <Header as="h3">
              Raw API Response
              <Button
                size="mini"
                floated="right"
                onClick={() => copyToClipboard(apiResponse)}
              >
                <Icon name="copy" />
                Copy Full Response
              </Button>
            </Header>
            <pre style={{
              backgroundColor: '#f8f9fa',
              padding: '1em',
              borderRadius: '4px',
              overflow: 'auto',
              maxHeight: '400px',
              fontSize: '11px'
            }}>
              {JSON.stringify(apiResponse, null, 2)}
            </pre>
          </Segment>
        )}
      </Modal.Content>

      <Modal.Actions>
        <Button onClick={onClose}>
          <Icon name="close" />
          Close
        </Button>
        <Button color="blue" onClick={() => copyToClipboard({ summary, characters })}>
          <Icon name="copy" />
          Copy Summary
        </Button>
      </Modal.Actions>
    </Modal>
  );
};

export default EnhancedCharactersDiagnostic;
